import { Suspense, lazy } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import type { BreadcrumbHandle } from '@/components/layout/use-breadcrumbs';
import { AppShell } from '@/layouts/AppShell';
import { NotFoundPage } from '@/pages/errors/NotFoundPage';
import { RouteErrorPage } from '@/pages/errors/RouteErrorPage';
import { GuestGuard } from '@/components/guards/GuestGuard';
import { AuthGuard } from '@/components/guards/AuthGuard';
// W21-F1-S6 — role-gated route guard (kept eager — small wrapper used everywhere).
import { RequireRole } from '@/lib/auth/RequireRole';
import ForbiddenPage from '@/routes/pages/Forbidden';
import Login from '@/routes/pages/Login';

// W11-F6-S2 — every authenticated page is lazy-loaded so the eager App
// bundle stays under the perf-budget gate. AppShell provides a Suspense
// boundary around `<Outlet />` for the default fallback; routes that
// need a different fallback wrap with their own `<Suspense>` below.

const Dashboard = lazy(() => import('@/routes/pages/Dashboard'));
// W47-F1 — Public pricing RFQ surface
const PricingPage = lazy(() => import('@/pages/pricing/PricingPage'));
// W143 — Cross-building planner (orders rollup + filters + bulk update)
const PlannerPage = lazy(() => import('@/pages/planner/PlannerPage'));
// W148-F2-S1 — Public FAQ surface
const FaqPage = lazy(() => import('@/pages/faq/FaqPage'));
// W148-F2-S2 — Public contact form
const ContactPage = lazy(() => import('@/pages/contact/ContactPage'));
// W102-F3-S2 — Cross-org deep-link 403 page
const CrossOrgForbidden = lazy(() => import('@/pages/errors/CrossOrgForbidden'));
// W137-F4 — public LP investor portal (opaque-token credentialed).
const LpPortalPage = lazy(() => import('@/pages/lp-portal/LpPortalPage'));
// W137-F5-S2 — JWT-gated investor portal (partner admins + future lp_viewer JWT flow).
const InvestorPortalPage = lazy(() => import('@/pages/investor/InvestorPortalPage'));
// W140-F3-S1 — Evidence pack builder (list / new / detail).
const EvidencePacksListPage = lazy(
  () => import('@/pages/evidence-packs/EvidencePacksListPage'),
);
const EvidencePacksNewPage = lazy(
  () => import('@/pages/evidence-packs/EvidencePacksNewPage'),
);
const EvidencePackDetailPage = lazy(
  () => import('@/pages/evidence-packs/EvidencePackDetailPage'),
);
// W48 — ASHRAE building-data capture (10 sub-pages + overview)
const AshraeBuildingDataPage = lazy(
  () => import('@/pages/ashrae/AshraeBuildingDataPage'),
);
// W50 — Matterport 3D-scan viewer
const BuildingScansPage = lazy(() => import('@/pages/matterport/BuildingScansPage'));
// W47-F3 — Self-serve onboarding wizard
const SelfServeOnboardingWizard = lazy(
  () => import('@/pages/onboarding/SelfServeOnboardingWizard'),
);
const EvaluationListPage = lazy(
  () => import('@/pages/breeam/EvaluationListPage'),
);
// W19-F2-S3 — generic per-framework evaluation route. Replaces the
// BREEAM-specific page mount; BREEAM continues to render via the
// registry (`selfManaged: true`) so behaviour stays bit-identical.
const EvaluationPage = lazy(() => import('@/pages/evaluations/EvaluationPage'));
// W129-F4-S3 — side-by-side compare two evaluations.
const EvaluationComparisonPage = lazy(
  () => import('@/pages/evaluations/EvaluationComparisonPage'),
);
const PortfoliosListPage = lazy(
  () => import('@/pages/portfolios/PortfoliosListPage'),
);
const PortfolioDetailPage = lazy(
  () => import('@/pages/portfolios/PortfolioDetailPage'),
);
// #1687 stage 2 — drill-down for the partner-edit Asset holders sub-tab.
const AssetHolderDetailPage = lazy(
  () => import('@/pages/asset-holders/AssetHolderDetailPage'),
);
const OrgTreePage = lazy(() => import('@/pages/orgs/OrgTreePage'));
// W71-F2-S2 — org-level REC / carbon-offset inventory + reconciliation UX.
const RecInventoryPage = lazy(() => import('@/pages/recs/RecInventoryPage'));
const CarbonOffsetInventoryPage = lazy(
  () => import('@/pages/recs/CarbonOffsetInventoryPage'),
);
const RecReconciliationPage = lazy(
  () => import('@/pages/recs/RecReconciliationPage'),
);
const BuildingOwnershipEditorPage = lazy(
  () => import('@/pages/orgs/BuildingOwnershipEditorPage'),
);
const BuildingsListPage = lazy(
  () => import('@/pages/buildings/BuildingsListPage'),
);
const BuildingDetailPage = lazy(
  () => import('@/pages/buildings/BuildingDetailPage'),
);
// W54-F4-S2 (#250) — EPC extraction review + apply-to-W7-EPC-scorer
const EpcReviewPage = lazy(
  () => import('@/pages/buildings/evidence/EpcReviewPage'),
);
// W40-F3 — lease & tenancy management UX
const LeaseListPage = lazy(() => import('@/pages/leases/LeaseListPage'));
const LeaseDetailPage = lazy(() => import('@/pages/leases/LeaseDetailPage'));
// W54-F3-S2 (#248) — review a lease extraction + create a W40 lease from it.
const LeaseReviewPage = lazy(() => import('@/pages/buildings/evidence/LeaseReviewPage'));
// W73-F1-S2 — insurance-policy management UX + renewal calendar.
const InsurancePolicyListPage = lazy(
  () => import('@/pages/insurance/InsurancePolicyListPage'),
);
const InsurancePolicyDetailPage = lazy(
  () => import('@/pages/insurance/InsurancePolicyDetailPage'),
);
const InsurancePolicyFormPage = lazy(
  () => import('@/pages/insurance/InsurancePolicyFormPage'),
);
const RenewalCalendarPage = lazy(
  () => import('@/pages/insurance/RenewalCalendarPage'),
);
// W73-F3-S2 — insurance loss-ratio + renewal-trend analytics dashboards.
const LossRatioPage = lazy(
  () => import('@/pages/portfolios/LossRatioPage'),
);
const RenewalTrendPage = lazy(
  () => import('@/pages/portfolios/RenewalTrendPage'),
);
// W79-F5-S2 — payout-ledger table + summary cards + per-entry detail drawer.
const PayoutLedgerPage = lazy(
  () => import('@/pages/payout-ledger/PayoutLedgerPage'),
);
// W42-F1-S2 — AP-invoice approval queue + contract-match detail drawer.
const ApInvoiceQueuePage = lazy(
  () => import('@/pages/ap-invoices/ApInvoiceQueuePage'),
);
// W63-F3-S1 — disposition transaction-room list + buyer-access detail.
const TransactionRoomListPage = lazy(
  () => import('@/pages/transaction-room/TransactionRoomListPage'),
);
const TransactionRoomDetailPage = lazy(
  () => import('@/pages/transaction-room/TransactionRoomDetailPage'),
);
// W41-F1 / W41-F6 — Owner Home dispatcher (persona-aware). The dispatcher
// composes the investment-flavoured OwnerHomePage and the
// operational-flavoured OperationalOwnerHomePage, and persists an
// explicit toggle in UserPreference.
const OwnerRoute = lazy(() => import('@/pages/owner/OwnerRoute'));
// W41-F4 — Cross-fund comparison
const CrossFundComparisonPage = lazy(
  () => import('@/pages/owner/CrossFundComparisonPage'),
);
const BuildingFinancePage = lazy(
  () => import('@/features/finance/pages/BuildingFinancePage'),
);
const PortfolioFinancePage = lazy(
  () => import('@/features/finance/pages/PortfolioFinancePage'),
);
// W72-F2-S2 — cash-forecast dashboard (per-fund / per-asset) + variance.
const CashForecastDashboardPage = lazy(
  () => import('@/pages/cash-forecast/CashForecastDashboardPage'),
);
const CashForecastVariancePage = lazy(
  () => import('@/pages/cash-forecast/CashForecastVariancePage'),
);
const BuildingOperationsPage = lazy(
  () => import('@/features/operations/pages/BuildingOperationsPage'),
);
// W79-F10-S2 (#505) — partner-admin tenant-workflows approval queue
const BuildingTenantWorkflowsPage = lazy(
  () =>
    import(
      '@/features/tenant-workflows-admin/pages/BuildingTenantWorkflowsPage'
    ),
);
// W141-F1-S1 — building-scoped interventions list
const BuildingInterventionsListPage = lazy(
  () =>
    import(
      '@/features/interventions/pages/BuildingInterventionsListPage'
    ),
);
// W141-F1-S2 — intervention detail page (read view + status actions)
const BuildingInterventionDetailPage = lazy(
  () =>
    import(
      '@/features/interventions/pages/BuildingInterventionDetailPage'
    ),
);
const WorkOrderDetailPage = lazy(
  () => import('@/features/operations/pages/WorkOrderDetailPage'),
);
const MaintenanceSchedulePage = lazy(
  () => import('@/features/operations/pages/MaintenanceSchedulePage'),
);
const IncidentListPage = lazy(
  () => import('@/features/operations/pages/IncidentListPage'),
);
const InspectionFindingDetailPage = lazy(
  () => import('@/features/operations/pages/InspectionFindingDetailPage'),
);
const ComplianceObligationDetailPage = lazy(
  () => import('@/features/operations/pages/ComplianceObligationDetailPage'),
);
const SlaBreachDashboardPage = lazy(
  () => import('@/features/operations/pages/SlaBreachDashboardPage'),
);
const PermitListPage = lazy(
  () => import('@/features/operations/pages/PermitListPage'),
);
const PermitDetailPage = lazy(
  () => import('@/features/operations/pages/PermitDetailPage'),
);
const EnergyPage = lazy(() => import('@/features/energy/pages/EnergyPage'));
const MeasureMvDetailPage = lazy(
  () => import('@/features/energy/pages/MeasureMvDetailPage'),
);
const QueryBuilderPage = lazy(
  () => import('@/features/reports/pages/QueryBuilderPage'),
);
const ExamplesGalleryPage = lazy(
  () => import('@/features/reports/pages/ExamplesGalleryPage'),
);
const SavedQueriesPage = lazy(
  () => import('@/features/reports/pages/SavedQueriesPage'),
);
const SavedQueryDetailPage = lazy(
  () => import('@/features/reports/pages/SavedQueryDetailPage'),
);
const ReportsIndexPage = lazy(
  () => import('@/features/reports/pages/ReportsIndexPage'),
);
const ExecutiveDashboardPage = lazy(
  () => import('@/features/dashboards/pages/ExecutiveDashboardPage'),
);
const CarbonDashboardPage = lazy(
  () => import('@/pages/analytics/CarbonDashboardPage'),
);
const BuildingsMapPage = lazy(
  () => import('@/pages/buildings/BuildingsMapPage'),
);
const SiteVisitsMapPage = lazy(
  () => import('@/pages/site-visits/SiteVisitsMapPage'),
);
const BuildingCreatePage = lazy(
  () => import('@/pages/buildings/BuildingCreatePage'),
);
const BuildingEditPage = lazy(
  () => import('@/pages/buildings/BuildingEditPage'),
);
const BuildingEvidencePage = lazy(
  () => import('@/pages/buildings/BuildingEvidencePage'),
);
const UtilityBillReviewPage = lazy(
  () => import('@/pages/buildings/evidence/UtilityBillReviewPage'),
);
const TenantTransparencyConfigPage = lazy(
  () => import('@/pages/orgs/TenantTransparencyConfigPage'),
);
const BuildingTasksPage = lazy(
  () => import('@/pages/buildings/BuildingTasksPage'),
);
// W65-F4-S2 (#352) — partner-admin amenity management + bookings overview.
const AmenitiesAdminPage = lazy(
  () => import('@/pages/buildings/AmenitiesAdminPage'),
);
// W145-F4-S1 — GHG audit report viewer (scope table + evidence pack linkage + PDF).
const GhgAuditReportPage = lazy(
  () => import('@/pages/ghg/GhgAuditReportPage'),
);
// W74-F4-S2 — Level(s) framework assessments list + detail (read-first).
const LevelsAssessmentsPage = lazy(
  () => import('@/pages/levels/LevelsAssessmentsPage'),
);
const LevelsAssessmentDetailPage = lazy(
  () => import('@/pages/levels/LevelsAssessmentDetailPage'),
);
// W74-F3-S2 — LCA emission-factor proposal review + manual override.
const LcaFactorsPage = lazy(() => import('@/pages/lca/factors-page'));
// W71-F4-S2 — Scope-2 market-based emissions proof report (read-only).
const EacScope2ProofReportPage = lazy(
  () => import('@/pages/eacs/EacScope2ProofReportPage'),
);
const BuildingTaskDetailPage = lazy(
  () => import('@/pages/buildings/BuildingTaskDetailPage'),
);
const NotificationCenterPage = lazy(
  () => import('@/pages/notifications/NotificationCenterPage'),
);
const NotificationPreferencesPage = lazy(
  () => import('@/pages/settings/NotificationPreferencesPage'),
);
const WebhookAdminPage = lazy(
  () => import('@/routes/pages/admin/webhooks/WebhookAdminPage'),
);
const TranslationsPage = lazy(
  () => import('@/pages/admin/translations/TranslationsPage'),
);
const PartnerSettingsPage = lazy(
  () => import('@/pages/admin/partner-settings/PartnerSettingsPage'),
);
const SettingsPage = lazy(() => import('@/pages/settings/SettingsPage'));
const ConnectorsSettingsPage = lazy(
  () => import('@/pages/settings/ConnectorsSettingsPage'),
);
const SupportTicketsListPage = lazy(
  () => import('@/pages/admin/support/SupportTicketsListPage'),
);
const SupportTicketDetailPage = lazy(
  () => import('@/pages/admin/support/SupportTicketDetailPage'),
);
// W47-F7-S1 — customer-facing /support page (distinct from /admin/support).
const CustomerSupportPage = lazy(() => import('@/pages/support/SupportPage'));
// #1533 A4 — /help index destination for the user-menu Help row
// (handbook APP_SHELL.md § 8.2.5 + PAGES.md 2.0.0).
const HelpIndexPage = lazy(() => import('@/pages/help/HelpIndexPage'));
const CustomerSupportTicketDetailPage = lazy(
  () => import('@/pages/support/SupportTicketDetailPage'),
);
const SystemConfigPage = lazy(
  () => import('@/pages/admin/system/SystemConfigPage'),
);
const ImportsListPage = lazy(
  () => import('@/pages/admin/imports/ImportsListPage'),
);
const ImportWizardPage = lazy(
  () => import('@/pages/admin/imports/ImportWizardPage'),
);
const ImportJobDetailPage = lazy(
  () => import('@/pages/admin/imports/ImportJobDetailPage'),
);
const ConnectorsListPage = lazy(
  () => import('@/pages/admin/imports/ConnectorsListPage'),
);
const AnalyticsListPage = lazy(
  () => import('@/pages/analytics/AnalyticsListPage'),
);
// W124-F4-S1b — benchmarking dashboard page.
const BenchmarkingPage = lazy(
  () => import('@/pages/analytics/BenchmarkingPage'),
);
const AnalyticsDashboardPage = lazy(
  () => import('@/pages/analytics/AnalyticsDashboardPage'),
);
const AnalyticsBuildingPage = lazy(
  () => import('@/pages/analytics/AnalyticsBuildingPage'),
);
const AnalyticsEditorPage = lazy(
  () => import('@/pages/analytics/AnalyticsEditorPage'),
);
const ReportBuilderPage = lazy(
  () => import('@/pages/analytics/ReportBuilderPage'),
);
const ScheduledReportsPage = lazy(
  () => import('@/pages/analytics/ScheduledReportsPage'),
);
// W5-F5 — partner onboarding + IdP self-service. Onboarding wizard ships
// a named export, so unwrap it for `lazy()`'s default-export contract.
const PartnerOnboardingWizard = lazy(() =>
  import('@/pages/onboarding/PartnerOnboardingWizard').then((m) => ({
    default: m.PartnerOnboardingWizard,
  })),
);
const IdentityProviderSettingsPage = lazy(
  () => import('@/pages/admin/settings/IdentityProviderSettingsPage'),
);
// W11-F9-S1 — partner API-key management
const ApiKeysPage = lazy(() => import('@/pages/admin/api-keys/ApiKeysPage'));
// W20-F1-S1 — partner-admin modules list (sortable + visibility toggle)
const ModulesPage = lazy(() => import('@/pages/admin/modules/ModulesPage'));
// W57-F2-S1 — "New module from text" authoring page (partner_admin only).
const NewModuleFromTextPage = lazy(
  () => import('@/pages/admin/modules/NewModuleFromTextPage'),
);
// W47-F5-S1 — read-only subscription tier + entitlements view
const SubscriptionPage = lazy(
  () => import('@/pages/admin/subscription/SubscriptionPage'),
);
// W20-F2-S1 — module edit page (4 tabs: general/presentation/activities/countriesVersions)
const ModuleEditPage = lazy(
  () => import('@/pages/admin/modules/ModuleEditPage'),
);
// W133-F3-S2 — AI token spend dashboard for operators.
const AiCostPage = lazy(() => import('@/pages/admin/ai-cost/AiCostPage'));
// #1647 — AI-assisted data ingestion front door (partner_admin / platform_admin).
const IngestPage = lazy(() => import('@/pages/admin/ingest/IngestPage'));
// W137-F1-S2 — utility connector registry admin.
const ConnectorsAdminPage = lazy(
  () => import('@/pages/admin/utility-connectors/ConnectorsAdminPage'),
);
// W143-F6 — utility connector ops console (health strip + DLQ).
const ConnectorOpsPage = lazy(
  () => import('@/pages/admin/utility-connectors/ConnectorOpsPage'),
);
// W146-F3-S1 — partner analytics dashboard (engagement + health tabs).
const PartnerAnalyticsPage = lazy(
  () => import('@/pages/admin/PartnerAnalyticsPage'),
);
// W16-F3-S1 — admin landing page
const AdminLandingPage = lazy(() => import('@/pages/admin/AdminLandingPage'));
// W22-F5-S2 / W22-F5-S3 — audit-log scoped pages (3 named exports from
// one module — load once, slice into three lazy components).
const auditLogModule = () => import('@/pages/admin/logs/AuditLogScopedPage');
const GlobalAuditLogPage = lazy(() =>
  auditLogModule().then((m) => ({ default: m.GlobalAuditLogPage })),
);
const AssetAuditLogPage = lazy(() =>
  auditLogModule().then((m) => ({ default: m.AssetAuditLogPage })),
);
const UserAuditLogPage = lazy(() =>
  auditLogModule().then((m) => ({ default: m.UserAuditLogPage })),
);
// W56-F4-S2 (#268) — evidence-classification audit surface.
const EvidenceClassificationAuditPage = lazy(
  () => import('@/pages/admin/audit/EvidenceClassificationAuditPage'),
);
// W11-F9-S2 — outbound webhook subscriptions admin
const WebhookSubscriptionsPage = lazy(
  () => import('@/pages/admin/webhooks/WebhookSubscriptionsPage'),
);
// W20-F7-S2 — v2-shape webhooks admin (will replace WebhookSubscriptionsPage in S3)
const WebhooksPage = lazy(() => import('@/pages/admin/webhooks/WebhooksPage'));
// W73-F4-S2 — carrier-API admin connection-management UX.
const CarrierApiConnectionsPage = lazy(
  () => import('@/features/carrier-api/pages/CarrierApiConnectionsPage'),
);
const CarrierApiConnectionDetailPage = lazy(
  () => import('@/features/carrier-api/pages/CarrierApiConnectionDetailPage'),
);
const CarrierApiSetupPage = lazy(
  () => import('@/features/carrier-api/pages/CarrierApiSetupPage'),
);
// W72-F1-S2 — bank-account linkage UX + transaction-sync view over the
// W72-F1-S1 banking-provider abstraction.
const BankAccountsPage = lazy(
  () => import('@/features/banking/pages/BankAccountsPage'),
);
const BankingLinkPage = lazy(
  () => import('@/features/banking/pages/BankingLinkPage'),
);
const BankAccountDetailPage = lazy(
  () => import('@/features/banking/pages/BankAccountDetailPage'),
);
const CarrierSyncConflictsPage = lazy(
  () => import('@/features/carrier-api/pages/CarrierSyncConflictsPage'),
);
// W21-F2-S2 — user lifecycle admin (4 views)
const UsersAdminLayout = lazy(
  () => import('@/pages/admin/users/UsersAdminLayout'),
);
const PendingUsersPage = lazy(() => import('@/pages/admin/users/PendingPage'));
const NonValidatedUsersPage = lazy(
  () => import('@/pages/admin/users/NonValidatedPage'),
);
const AuthorizedUsersPage = lazy(
  () => import('@/pages/admin/users/AuthorizedPage'),
);
const BlockedUsersPage = lazy(() => import('@/pages/admin/users/BlockedPage'));
const AdminUserEditPage = lazy(
  () => import('@/pages/admin/users/AdminUserEditPage'),
);
// W21-F2-S3 — invitation accept-landing
const AcceptInvitationPage = lazy(
  () => import('@/pages/auth/AcceptInvitationPage'),
);
// W85-F4 (Phase 15) — Pilot SLA dashboard (platform_admin only)
const PilotSlaDashboardPage = lazy(
  () => import('@/pages/admin/sre/PilotSlaDashboardPage'),
);

// W51-F3-S2 — per-customer ETL migration console (platform_admin only).
const MigrationsPage = lazy(
  () => import('@/pages/admin/migrations/MigrationsPage'),
);
const MigrationDetailPage = lazy(
  () => import('@/pages/admin/migrations/MigrationDetailPage'),
);
// W51-F4-S2 — per-customer migration-readiness dashboard (platform_admin only).
const ReadinessDashboard = lazy(
  () => import('@/pages/admin/migrations/ReadinessDashboard'),
);

// W19-F1-S2 — top-level cross-framework modules listing.
const ModulesListPage = lazy(() => import('@/pages/modules/ModulesListPage'));

// W67-F2-S1 — partner-admin compliance calendar (month/quarter/year views).
const ComplianceCalendarPage = lazy(
  () => import('@/pages/compliance/CalendarPage'),
);
// W137-F6-S2 — AI compliance assistant chat.
const ComplianceAssistantPage = lazy(
  () => import('@/pages/compliance/ComplianceAssistantPage'),
);
// W56-F2-S1 — evidence classification inbox (suggest/manual + accept/override).
const EvidenceInboxPage = lazy(
  () => import('@/pages/evidence/EvidenceInboxPage'),
);

// W56-F2-S2 — auto-applied evidence classification audit.
const AutoAppliedAuditPage = lazy(() => import('@/pages/evidence/AutoAppliedAuditPage'));

// W56-F3-S2 — partner-admin bulk reclassify (preview-then-apply).
const BulkReclassificationPage = lazy(
  () => import('@/pages/admin/BulkReclassificationPage'),
);

// #1425 / W39-F4-S1 — assessor-assignment workflow (my-queue + detail).
// Route-only surface (no nav item yet).
const AssessorQueuePage = lazy(
  () => import('@/features/assessor-workflow/pages/AssessorQueuePage'),
);
const AssessorAssignmentDetailPage = lazy(
  () =>
    import('@/features/assessor-workflow/pages/AssessorAssignmentDetailPage'),
);
// W77-F1 / W77-F2 — acquisitions pipeline + deal detail (Phase 14)
const AcquisitionsPipelinePage = lazy(
  () => import('@/features/acquisitions/pages/AcquisitionsPipelinePage'),
);
const DealDetailPage = lazy(
  () => import('@/features/acquisitions/pages/DealDetailPage'),
);
// W88-F2 — multi-step deal create wizard (Phase 15). Replaces the
// minimal "+ New deal" sheet shipped under W77 PR A.
const DealCreateWizard = lazy(
  () => import('@/features/acquisitions/pages/DealCreateWizard'),
);
// W77-F3-S2 — org-wide counterparty roster (Phase 14).
const CounterpartiesRosterPage = lazy(
  () => import('@/features/acquisitions/pages/CounterpartiesRosterPage'),
);
// W73-F2-S2 — insurance claims tracking (Phase 13).
const PolicyClaimsPage = lazy(
  () => import('@/features/insurance-claims/pages/PolicyClaimsPage'),
);
const ClaimDetailPage = lazy(
  () => import('@/features/insurance-claims/pages/ClaimDetailPage'),
);
const ClaimFormPage = lazy(
  () => import('@/features/insurance-claims/pages/ClaimFormPage'),
);
const AssetClaimHistoryPage = lazy(
  () => import('@/features/insurance-claims/pages/AssetClaimHistoryPage'),
);
// W72-F4-S2 — distribution payment workflow (Phase 12).
const FundDistributionsPage = lazy(
  () => import('@/features/distributions/pages/FundDistributionsPage'),
);
const DistributionDetailPage = lazy(
  () => import('@/features/distributions/pages/DistributionDetailPage'),
);
// W77 PR C — standalone valuation surfaces (Phase 14).
// W60-F5-S2 (#304) — workspace-level valuation index at `/valuation`.
const ValuationListPage = lazy(
  () => import('@/features/valuation/pages/ValuationListPage'),
);
const ValuationRunPage = lazy(
  () => import('@/features/valuation/pages/ValuationRunPage'),
);
const ValuationScenariosPage = lazy(
  () => import('@/features/valuation/pages/ValuationScenariosPage'),
);
// W61-F2-S2 — dedicated building-scoped sell-vs-refinance comparison surface.
const SellVsRefiPage = lazy(
  () => import('@/features/valuation/pages/SellVsRefiPage'),
);
// W77 PR D — dispositions pipeline + transaction-room (Phase 14).
const DispositionsListPage = lazy(
  () => import('@/features/dispositions/pages/DispositionsListPage'),
);
const DispositionDetailPage = lazy(
  () => import('@/features/dispositions/pages/DispositionDetailPage'),
);
// W88-F3 — multi-step disposition create wizard (Phase 15). Replaces
// the minimal "+ New disposition" sheet shipped under W77 PR D.
const DispositionCreateWizard = lazy(
  () => import('@/features/dispositions/pages/DispositionCreateWizard'),
);
// W63-F1-S1 — disposition sale-prep workspace (singular `disposition`
// module). Engagement list + detail + sale-prep checklist with progress.
// Distinct from the W77 `dispositions` pipeline above.
const EngagementListPage = lazy(
  () => import('@/features/disposition-sale-prep/pages/EngagementListPage'),
);
const EngagementDetailPage = lazy(
  () => import('@/features/disposition-sale-prep/pages/EngagementDetailPage'),
);
// W67-F3-S2 — org-level defaults for reminder offsets + framework assignees.
const CalendarDefaultsPage = lazy(
  () => import('@/pages/admin/compliance-calendar/CalendarDefaultsPage'),
);

// W66-F4-S1 — workflows list page (partner-admin).
const WorkflowsListPage = lazy(
  () => import('@/pages/workflows/WorkflowsListPage'),
);
// W66-F4-S2 — visual workflow builder.
const WorkflowBuilderPage = lazy(
  () => import('@/pages/workflows/WorkflowBuilderPage'),
);
// W66-F5-S2 — run history list + per-run drilldown.
const WorkflowRunsPage = lazy(
  () => import('@/pages/workflows/WorkflowRunsPage'),
);
const WorkflowRunDetailPage = lazy(
  () => import('@/pages/workflows/WorkflowRunDetailPage'),
);

// W78 — Construction + Development FE (Phase 14). Project list + 6-tab
// detail page over the W62-F1..F6 backends already on main.
const ProjectsListPage = lazy(
  () => import('@/features/construction/pages/ProjectsListPage'),
);
const ProjectDetailPage = lazy(
  () => import('@/features/construction/pages/ProjectDetailPage'),
);
// W88-F1 — multi-step project create wizard (Phase 15). Routed
// alongside the rest of /projects.
const ProjectCreateWizard = lazy(
  () => import('@/features/construction/pages/ProjectCreateWizard'),
);

// W86-F4 — partner-admin tenant-request console (Phase 15).
const TenantRequestsAdminPage = lazy(
  () =>
    import('@/features/tenant-requests-admin/pages/TenantRequestsAdminPage'),
);

// W79 PR A — Module Marketplace FE (Phase 14). Five pages over the W64
// backends already on main: browse, listing detail (+ reviews), curator
// queue, per-org installs, author payouts/Stripe-Connect onboarding.
const MarketplaceBrowsePage = lazy(
  () => import('@/pages/marketplace/MarketplaceBrowsePage'),
);
const MarketplaceListingDetailPage = lazy(
  () => import('@/pages/marketplace/MarketplaceListingDetailPage'),
);
const MarketplaceCuratorQueuePage = lazy(
  () => import('@/pages/marketplace/MarketplaceCuratorQueuePage'),
);
const MarketplaceInstallsPage = lazy(
  () => import('@/pages/marketplace/MarketplaceInstallsPage'),
);
const MarketplacePayoutsPage = lazy(
  () => import('@/pages/marketplace/MarketplacePayoutsPage'),
);
const PublishListingPage = lazy(
  () => import('@/pages/marketplace/PublishListingPage'),
);
const MyListingsPage = lazy(
  () => import('@/pages/marketplace/MyListingsPage'),
);

// W20-F3-S3 — module-template authoring routes (lazy so the editor
// bundle ships only on first navigation). Bodies are stubs in S3 and
// fill in across S4 (list), S5 (editor shell), S6-S8 (tree editors).
const ModuleTemplatesPage = lazy(
  () => import('@/pages/admin/templates/ModuleTemplatesPage'),
);
// W20-F5-S2 / W20-F5-S3 — recurring-variable + variable-area admin surfaces.
const VariableAreasPage = lazy(() =>
  import('@/pages/admin/variable-areas/VariableAreasPage').then((m) => ({
    default: m.VariableAreasPage,
  })),
);
const RecurringVariablesPage = lazy(() =>
  import('@/pages/admin/recurring-variables/RecurringVariablesPage').then((m) => ({
    default: m.RecurringVariablesPage,
  })),
);
const ModuleTemplateEditorPage = lazy(
  () => import('@/pages/admin/templates/ModuleTemplateEditorPage'),
);
const ModuleTemplatePreviewPage = lazy(
  () => import('@/pages/admin/templates/ModuleTemplatePreviewPage'),
);
const TemplatesAvailablePage = lazy(
  () => import('@/pages/admin/templates/TemplatesAvailablePage'),
);
const ModuleTemplateVersionsPage = lazy(
  () => import('@/pages/admin/templates/ModuleTemplateVersionsPage'),
);

// W22-F2-S2 — admin Languages registry (lazy; only workspace admins reach it).
const AdminLanguagesPage = lazy(
  () => import('@/pages/admin/languages/AdminLanguagesPage'),
);

// #1546 — admin Measurement Units catalog (lazy; partner_admin+).
const AdminMeasurementUnitsPage = lazy(
  () => import('@/pages/admin/measurement-units/AdminMeasurementUnitsPage'),
);

// #1545 — admin Currencies catalog (lazy; only workspace admins reach it).
const AdminCurrenciesPage = lazy(
  () => import('@/pages/admin/currencies/AdminCurrenciesPage'),
);

// #1547 — admin Tags catalog (lazy; partner_admin+).
const AdminTagsPage = lazy(
  () => import('@/pages/admin/tags/AdminTagsPage'),
);

// #1550 — global time-travel override (lazy; platform_admin only).
const AdminTimeTravelPage = lazy(
  () => import('@/pages/admin/time-travel/AdminTimeTravelPage'),
);

// #1548 — EU Taxonomy per-country benchmarks editor (platform_admin only).
const AdminEuTaxonomyBenchmarksPage = lazy(
  () => import('@/pages/admin/eu-taxonomy-benchmarks/AdminEuTaxonomyBenchmarksPage'),
);

// #1549 — EU Taxonomy XLS regression harness (lazy; platform_admin only).
const AdminEuTaxonomyTestRunsPage = lazy(
  () =>
    import(
      '@/pages/admin/eu-taxonomy-test-runs/AdminEuTaxonomyTestRunsPage'
    ),
);

// W53-F3-S2 — NL-query pipeline probe (lazy; partner_admin / platform_admin).
const NlQueryProbePage = lazy(
  () => import('@/pages/admin/nl-query-probe/NlQueryProbePage'),
);

// W22-F4-S1 — partner branding editor (lazy; partner_admin only).
const BrandingPage = lazy(
  () => import('@/pages/admin/branding/BrandingPage'),
);

// W22-F9-S1 — partner CRUD admin page (lazy; partner_admin / platform_admin).
const AdminPartnersPage = lazy(
  () => import('@/pages/admin/partners/AdminPartnersPage'),
);
const PartnerEditPage = lazy(
  () => import('@/pages/admin/partners/PartnerEditPage'),
);

// W19-F7-S1 — EU Taxonomy routes (lazy so the per-asset bundle does not
// bloat the building-detail load). Bodies are stubs in S1 and become
// real pages in S6 (per-asset), S9 (admin orders), and S10 (portfolio).
const EuTaxonomyPage = lazy(() => import('@/pages/eu-taxonomy/EuTaxonomyPage'));
const EuTaxonomyPortfolioPage = lazy(
  () => import('@/pages/eu-taxonomy/EuTaxonomyPortfolioPage'),
);
const LcaPortfolioPage = lazy(() => import('@/pages/lca/LcaPortfolioPage'));
const EuTaxonomyOrdersPage = lazy(
  () => import('@/pages/admin/EuTaxonomyOrdersPage'),
);

// W47-F6 — generic /admin/orders landing covering all 5 order types.
const AdminOrdersListPage = lazy(
  () => import('@/pages/admin/orders/AdminOrdersListPage'),
);
const AdminOrderDetailPage = lazy(
  () => import('@/pages/admin/orders/AdminOrderDetailPage'),
);

// W79 PR B — tenant portal subtree. Mounted outside the partner AuthGuard
// because tenant-tier users authenticate via a separate HS256 JWT
// (POST /api/v1/tenant/sign-in), not MSAL. Layout enforces the
// tenant-session route guard client-side; @TenantOnly() on the
// backend is the authoritative gate.
const TenantLayoutLazy = lazy(() => import('@/pages/tenant/TenantLayout'));
const TenantSignInPage = lazy(() => import('@/pages/tenant/TenantSignInPage'));
const TenantHomePage = lazy(() => import('@/pages/tenant/TenantHomePage'));
const TenantRequestsPage = lazy(
  () => import('@/pages/tenant/TenantRequestsPage'),
);
const TenantNewRequestPage = lazy(
  () => import('@/pages/tenant/requests/NewRequestPage'),
);
const TenantTransparencyPage = lazy(
  () => import('@/pages/tenant/TenantTransparencyPage'),
);
// W79-F8-S2 (#521) — per-section ESG deep-dive (chart + narrative + actions).
const TenantEsgSectionDeepDivePage = lazy(
  () => import('@/pages/tenant/esg/SectionDeepDivePage'),
);
const TenantAmenitiesPage = lazy(
  () => import('@/pages/tenant/TenantAmenitiesPage'),
);
const TenantWorkflowsPage = lazy(
  () => import('@/pages/tenant/TenantWorkflowsPage'),
);

function PageSuspenseFallback(): JSX.Element {
  return (
    <div className="space-y-2 rounded-md border p-4" role="status" aria-busy="true">
      <div className="h-8 w-1/3 animate-pulse rounded bg-muted" />
      <div className="h-32 w-full animate-pulse rounded bg-muted" />
    </div>
  );
}

/**
 * Router.
 *
 * Routes:
 *   `/`            — public marketing landing (GuestGuard)
 *   `/login`       — sign-in stub        (GuestGuard); real wiring in W1-F6
 *   `/dashboard`   — auth-gated, AppShell (AuthGuard); Core demo lands W1-F12
 *   `/buildings`, `/portfolios`, `/orgs` — W4 surfaces.
 *   `/buildings/:buildingId/evaluations/...` — W3 BREEAM evaluation flow.
 *   `/buildings/:buildingId/{tasks,evidence}` — W6 tasks + evidence.
 *   `/notifications`, `/settings/notifications` — W6 notification center.
 *   `/analytics/...` — W8 analytics surface (list, editor, report builder,
 *   scheduled, per-building drill-down).
 *   `/admin/integrations/webhooks` — W9-F2-S2 admin webhook replay UI.
 *   placeholder for `/reports` lands in later waves.
 *
 * Both guards are pass-through stubs in W1; W1-F6 swaps them for real MSAL-aware
 * variants.
 */
export const router = createBrowserRouter([
  {
    // Root redirects unauthenticated visitors to /login (where the
    // canonical auth surface lives per sustevo-handbook brand/AUTH_PAGES.md).
    // GuestGuard short-circuits authenticated users to /dashboard before
    // the Navigate fires, so this remains the single entry point.
    path: '/',
    element: (
      <GuestGuard>
        <Navigate to="/login" replace />
      </GuestGuard>
    ),
  },
  {
    path: '/login',
    element: (
      <GuestGuard>
        <Login />
      </GuestGuard>
    ),
  },
  // W137-F4 — public LP investor portal. NO GuestGuard: LPs have no
  // Entra account, so redirecting to /login on token failure would
  // dead-end them. The page renders its own NotFoundCard for invalid
  // tokens (BE returns 404 on revoke/expire/soft-delete).
  {
    path: '/lp-portal/:token',
    element: (
      <Suspense fallback={null}>
        <LpPortalPage />
      </Suspense>
    ),
  },
  // W137-F5-S2 — JWT-gated investor portal. Uses AuthGuard (Entra) for partner
  // admins / managers. LP users (lp_viewer role) will need the invite→password
  // auth flow (future work). No AppShell: LP view has its own minimal layout.
  {
    path: '/investor/:fundSlug',
    element: (
      <AuthGuard>
        <Suspense fallback={null}>
          <InvestorPortalPage />
        </Suspense>
      </AuthGuard>
    ),
  },
  // W47-F1 — public pricing surface (RFQ-only).
  {
    path: '/pricing',
    element: (
      <GuestGuard>
        <Suspense fallback={null}>
          <PricingPage />
        </Suspense>
      </GuestGuard>
    ),
  },
  // W148-F2-S1 — public FAQ. No guard: accessible signed-in OR out so
  // prospects can read it before sign-up, and authed users can deep-link
  // to it from /support without bouncing through GuestGuard.
  {
    path: '/faq',
    element: (
      <Suspense fallback={null}>
        <FaqPage />
      </Suspense>
    ),
  },
  // W148-F2-S2 — public contact form. NO guard: accessible signed-in
  // OR out so prospects can reach us before sign-up + authed users
  // who'd rather email than file a support ticket can use it.
  {
    path: '/contact',
    element: (
      <Suspense fallback={null}>
        <ContactPage />
      </Suspense>
    ),
  },
  // W47-F3 — self-serve customer onboarding wizard.
  {
    path: '/sign-up',
    element: (
      <GuestGuard>
        <Suspense fallback={null}>
          <SelfServeOnboardingWizard />
        </Suspense>
      </GuestGuard>
    ),
  },
  {
    element: (
      <AuthGuard>
        <AppShell />
      </AuthGuard>
    ),
    // Canonical route-level error boundary per
    // `sustevo-handbook/brand/ERROR_PAGES.md` § 2. Catches throws from
    // any descendant loader / action / element render so the user sees
    // the branded error surface rather than the React-Router default.
    errorElement: <RouteErrorPage />,
    children: [
      { path: '/dashboard', element: <Dashboard /> },
      // W140-F3-S1 — Evidence pack builder.
      { path: '/evidence-packs', element: <EvidencePacksListPage /> },
      { path: '/evidence-packs/new', element: <EvidencePacksNewPage /> },
      { path: '/evidence-packs/:packId', element: <EvidencePackDetailPage /> },
      // W4-F2 — Portfolios.
      {
        path: '/portfolios',
        element: <PortfoliosListPage />,
        handle: { breadcrumb: 'nav.portfolios' } satisfies BreadcrumbHandle,
      },
      {
        path: '/portfolios/:portfolioId',
        element: <PortfolioDetailPage />,
        handle: {
          breadcrumb: (m) => `Portfolio #${(m.params as { portfolioId?: string }).portfolioId ?? ''}`,
        } satisfies BreadcrumbHandle,
      },
      // #1687 stage 2 — drill-down for the partner-edit Asset holders sub-tab
      {
        path: '/asset-holders/:id',
        element: <AssetHolderDetailPage />,
        handle: {
          breadcrumb: (m) => `Holder #${(m.params as { id?: string }).id ?? ''}`,
        } satisfies BreadcrumbHandle,
      },
      // W4-F3 — Organizations view + Ownership editor.
      { path: '/orgs', element: <OrgTreePage /> },
      // W71-F2-S2 — org-level REC / carbon-offset inventory + reconciliation.
      // `/reconciliation` precedes the bare inventory route so it wins the
      // match. All three are route-only (no sidebar nav item).
      {
        path: '/orgs/:orgId/recs/reconciliation',
        element: <RecReconciliationPage />,
        handle: { breadcrumb: 'recs.reconciliation.title' } satisfies BreadcrumbHandle,
      },
      {
        path: '/orgs/:orgId/recs',
        element: <RecInventoryPage />,
        handle: { breadcrumb: 'recs.inventory.title' } satisfies BreadcrumbHandle,
      },
      {
        path: '/orgs/:orgId/carbon-offsets',
        element: <CarbonOffsetInventoryPage />,
        handle: { breadcrumb: 'offsets.inventory.title' } satisfies BreadcrumbHandle,
      },
      {
        path: '/buildings/:buildingId/ownership',
        element: <BuildingOwnershipEditorPage />,
      },
      // W4-F1 — Buildings list / detail / map / create / edit.
      {
        path: '/buildings',
        element: <BuildingsListPage />,
        handle: { breadcrumb: 'nav.buildings' } satisfies BreadcrumbHandle,
      },
      // W143 — Cross-building planner (cross-portfolio orders view).
      {
        path: '/planner',
        element: (
          <Suspense fallback={null}>
            <PlannerPage />
          </Suspense>
        ),
        handle: { breadcrumb: 'nav.planner' } satisfies BreadcrumbHandle,
      },
      { path: '/buildings/map', element: <BuildingsMapPage /> },
      // W45-F5-S2 (#1477) — site-visit map (recent visits over the shared map).
      { path: '/site-visits/map', element: <SiteVisitsMapPage /> },
      { path: '/buildings/new', element: <BuildingCreatePage /> },
      {
        path: '/buildings/:buildingId',
        element: <BuildingDetailPage />,
        handle: {
          breadcrumb: (m) => `Building #${(m.params as { buildingId?: string }).buildingId ?? ''}`,
        } satisfies BreadcrumbHandle,
      },
      { path: '/buildings/:buildingId/edit', element: <BuildingEditPage /> },
      // W54-F4-S2 (#250) — review an extracted EPC certificate, then apply it
      // to the W7 EPC evaluation. The extraction `jobId` arrives as a query param.
      {
        path: '/buildings/:buildingId/evidence/:evidenceFileId/extract/epc',
        element: <EpcReviewPage />,
        handle: {
          breadcrumb: 'epcReview.heading',
        } satisfies BreadcrumbHandle,
      },
      // W48 — ASHRAE building-data capture (10 sub-pages + overview)
      {
        path: '/buildings/:buildingId/ashrae',
        element: <AshraeBuildingDataPage />,
      },
      // W50 — Matterport 3D-scan viewer
      {
        path: '/buildings/:buildingId/3d-scan',
        element: <BuildingScansPage />,
      },
      // W12-F2-S1 — Phase 2 finance KPIs
      { path: '/buildings/:buildingId/finance', element: <BuildingFinancePage /> },
      // W61-F2-S2 — dedicated building-scoped sell-vs-refinance comparison.
      {
        path: '/buildings/:buildingId/sell-vs-refi',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <Suspense fallback={<PageSuspenseFallback />}>
              <SellVsRefiPage />
            </Suspense>
          </RequireRole>
        ),
        handle: {
          breadcrumb: 'valuation.sellVsRefi.title',
        } satisfies BreadcrumbHandle,
      },
      // W12-F3-S2 — Phase 2 portfolio finance rollup
      { path: '/portfolios/:portfolioId/finance', element: <PortfolioFinancePage /> },
      // W72-F2-S2 — cash-forecast dashboard + variance (read-only, route-only)
      {
        path: '/funds/:fundId/cash-forecast',
        element: <CashForecastDashboardPage scope="fund" />,
      },
      {
        path: '/funds/:fundId/cash-forecast/variance',
        element: <CashForecastVariancePage />,
      },
      {
        path: '/assets/:assetId/cash-forecast',
        element: <CashForecastDashboardPage scope="asset" />,
      },
      // W72-F4-S2 — distribution payment workflow (per-fund list + detail with
      // per-LP allocations and state-machine-gated transitions; route-only).
      {
        path: '/funds/:fundId/distributions',
        element: (
          <Suspense fallback={<PageSuspenseFallback />}>
            <FundDistributionsPage />
          </Suspense>
        ),
        handle: {
          breadcrumb: 'distributions.fundList.heading',
        } satisfies BreadcrumbHandle,
      },
      {
        path: '/distributions/:distributionId',
        element: (
          <Suspense fallback={<PageSuspenseFallback />}>
            <DistributionDetailPage />
          </Suspense>
        ),
        handle: {
          breadcrumb: 'distributions.detail.back',
        } satisfies BreadcrumbHandle,
      },
      // W13-F1-S1 — Phase 2 operations / work orders
      { path: '/buildings/:buildingId/operations', element: <BuildingOperationsPage /> },
      // W79-F10-S2 (#505) — partner-admin tenant-workflows approval queue
      {
        path: '/buildings/:buildingId/tenant-workflows',
        element: <BuildingTenantWorkflowsPage />,
      },
      // W141-F1-S1 — building-scoped intervention list
      {
        path: '/buildings/:buildingId/interventions',
        element: <BuildingInterventionsListPage />,
      },
      // W141-F1-S2 — intervention detail page
      {
        path: '/buildings/:buildingId/interventions/:id',
        element: <BuildingInterventionDetailPage />,
      },
      // W13-F1-S2 — WO detail + state-machine action bar
      { path: '/work-orders/:id', element: <WorkOrderDetailPage /> },
      // W13-F1-S3 — Maintenance schedule (next 90 days projection)
      {
        path: '/buildings/:buildingId/operations/schedule',
        element: <MaintenanceSchedulePage />,
      },
      // W13-F2-S1 — Incident list (overdue obligations + critical findings)
      { path: '/buildings/:buildingId/incidents', element: <IncidentListPage /> },
      // W13-F2-S2 — Per-source incident detail pages
      { path: '/incidents/findings/:id', element: <InspectionFindingDetailPage /> },
      { path: '/incidents/obligations/:id', element: <ComplianceObligationDetailPage /> },
      // W13-F3-S2 — SLA breach dashboard (per-building)
      { path: '/buildings/:buildingId/operations/sla', element: <SlaBreachDashboardPage /> },
      // W13-F3-S3 — Permits list + detail with state-machine actions
      { path: '/buildings/:buildingId/permits', element: <PermitListPage /> },
      { path: '/permits/:id', element: <PermitDetailPage /> },
      // W14-F1-S1 — Energy consumption page (Meters / Sensors / Benchmark / M&V)
      { path: '/buildings/:buildingId/energy', element: <EnergyPage /> },
      // W14-F3-S2 — Per-measure M&V detail
      { path: '/measures/:id/mv', element: <MeasureMvDetailPage /> },
      // W3-F3 — BREEAM evaluation routes.
      {
        path: '/buildings/:buildingId/evaluations',
        element: <EvaluationListPage />,
        handle: { breadcrumb: 'breeam.list_title' } satisfies BreadcrumbHandle,
      },
      // W19-F2-S3 — generic per-framework evaluation route. Resolves the
      // framework slug against `frameworkRegistry` and either mounts the
      // self-managed body (BREEAM) or wraps body+summary in
      // `<EvaluationShell>` (every other framework).
      {
        path: '/buildings/:buildingId/evaluations/:framework/:moduleId',
        element: <EvaluationPage />,
        handle: {
          breadcrumb: (m) => `Module #${(m.params as { moduleId?: string }).moduleId ?? ''}`,
        } satisfies BreadcrumbHandle,
      },
      // W129-F4-S3 — compare two evaluations (leftId + rightId query params).
      {
        path: '/buildings/:buildingId/evaluations/compare',
        element: <EvaluationComparisonPage />,
        handle: {
          breadcrumb: () => 'Compare',
        } satisfies BreadcrumbHandle,
      },
      // W41-F1 / W41-F6 — Owner Home dashboard (persona-aware dispatcher)
      {
        path: '/owner',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <OwnerRoute />
          </RequireRole>
        ),
        handle: { breadcrumb: 'owner.heading' } satisfies BreadcrumbHandle,
      },
      // W41-F4 — Cross-fund comparison
      {
        path: '/owner/funds',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <CrossFundComparisonPage />
          </RequireRole>
        ),
        handle: { breadcrumb: 'owner.crossFund.heading' } satisfies BreadcrumbHandle,
      },
      // W40-F3 — lease & tenancy management UX
      {
        path: '/buildings/:buildingId/leases',
        element: <LeaseListPage />,
        handle: { breadcrumb: 'leases.heading' } satisfies BreadcrumbHandle,
      },
      {
        path: '/buildings/:buildingId/leases/:leaseId',
        element: <LeaseDetailPage />,
        handle: {
          breadcrumb: (m) => `Lease ${(m.params as { leaseId?: string }).leaseId ?? ''}`,
        } satisfies BreadcrumbHandle,
      },
      // W54-F3-S2 (#248) — lease extraction review + create-lease.
      {
        path: '/buildings/:buildingId/evidence/:evidenceFileId/extract/lease',
        element: <LeaseReviewPage />,
        handle: { breadcrumb: 'leases.review.heading' } satisfies BreadcrumbHandle,
      },
      {
        path: '/portfolios/:portfolioId/leases',
        element: <LeaseListPage />,
        handle: { breadcrumb: 'leases.heading' } satisfies BreadcrumbHandle,
      },
      {
        path: '/portfolios/:portfolioId/leases/:leaseId',
        element: <LeaseDetailPage />,
        handle: {
          breadcrumb: (m) => `Lease ${(m.params as { leaseId?: string }).leaseId ?? ''}`,
        } satisfies BreadcrumbHandle,
      },
      // W73-F1-S2 — insurance-policy management UX + renewal calendar.
      {
        path: '/buildings/:buildingId/insurance',
        element: <InsurancePolicyListPage />,
        handle: { breadcrumb: 'insurance.list.heading' } satisfies BreadcrumbHandle,
      },
      {
        path: '/portfolios/:portfolioId/insurance',
        element: <InsurancePolicyListPage />,
        handle: { breadcrumb: 'insurance.list.heading' } satisfies BreadcrumbHandle,
      },
      // W73-F3-S2 — insurance loss-ratio + renewal-trend analytics dashboards.
      {
        path: '/portfolios/:portfolioId/insurance-analytics/loss-ratio',
        element: <LossRatioPage />,
        handle: {
          breadcrumb: 'insurance.analytics.lossRatio.heading',
        } satisfies BreadcrumbHandle,
      },
      {
        path: '/portfolios/:portfolioId/insurance-analytics/renewal-trend',
        element: <RenewalTrendPage />,
        handle: {
          breadcrumb: 'insurance.analytics.renewalTrend.heading',
        } satisfies BreadcrumbHandle,
      },
      {
        path: '/orgs/:orgId/insurance',
        element: <InsurancePolicyListPage />,
        handle: { breadcrumb: 'insurance.list.heading' } satisfies BreadcrumbHandle,
      },
      {
        path: '/orgs/:orgId/insurance/calendar',
        element: <RenewalCalendarPage />,
        handle: { breadcrumb: 'insurance.calendar.heading' } satisfies BreadcrumbHandle,
      },
      {
        path: '/orgs/:orgId/insurance/new',
        element: <InsurancePolicyFormPage />,
        handle: { breadcrumb: 'insurance.form.createHeading' } satisfies BreadcrumbHandle,
      },
      {
        path: '/policies/:policyId/edit',
        element: <InsurancePolicyFormPage />,
        handle: { breadcrumb: 'insurance.form.editHeading' } satisfies BreadcrumbHandle,
      },
      {
        path: '/policies/:policyId',
        element: <InsurancePolicyDetailPage />,
        handle: {
          breadcrumb: (m) =>
            `Policy ${(m.params as { policyId?: string }).policyId ?? ''}`,
        } satisfies BreadcrumbHandle,
      },
      // W79-F5-S2 — payout-ledger table + summary cards + per-entry detail drawer.
      {
        path: '/orgs/:orgId/payout-ledger',
        element: <PayoutLedgerPage />,
        handle: { breadcrumb: 'payoutLedger.heading' } satisfies BreadcrumbHandle,
      },
      // W42-F1-S2 — AP-invoice approval queue (tenancy via context, route-only).
      {
        path: '/ap-invoices',
        element: <ApInvoiceQueuePage />,
        handle: { breadcrumb: 'apInvoices.heading' } satisfies BreadcrumbHandle,
      },
      // W63-F3-S1 — disposition transaction-room list + buyer-access detail.
      {
        path: '/transaction-rooms',
        element: <TransactionRoomListPage />,
        handle: {
          breadcrumb: 'transactionRoom.heading',
        } satisfies BreadcrumbHandle,
      },
      {
        path: '/transaction-rooms/:roomId',
        element: <TransactionRoomDetailPage />,
        handle: {
          breadcrumb: 'transactionRoom.detail.breadcrumb',
        } satisfies BreadcrumbHandle,
      },
      // W6-F2 — evidence upload page for a building.
      {
        path: '/buildings/:buildingId/evidence',
        element: <BuildingEvidencePage />,
      },
      // W54-F2-S2 (#245) — utility-bill extraction review + apply-to-energy.
      {
        path: '/buildings/:buildingId/evidence/:evidenceFileId/extract/utility-bill',
        element: <UtilityBillReviewPage />,
      },
      // W65-F3-S2 — partner-admin config for the tenant ESG dashboard.
      {
        path: '/buildings/:buildingId/tenant-transparency',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <TenantTransparencyConfigPage />
          </RequireRole>
        ),
        handle: {
          breadcrumb: 'admin.orgs.tenant_transparency.heading',
        } satisfies BreadcrumbHandle,
      },
      // W145-F4-S1 — GHG audit report viewer.
      {
        path: '/buildings/:buildingId/ghg-reports/:reportId',
        element: <GhgAuditReportPage />,
      },
      // W74-F4-S2 — Level(s) framework assessments list + detail.
      {
        path: '/levels',
        element: <LevelsAssessmentsPage />,
        handle: { breadcrumb: 'levels.list.title' } satisfies BreadcrumbHandle,
      },
      {
        path: '/levels/:id',
        element: <LevelsAssessmentDetailPage />,
        handle: {
          breadcrumb: (m) =>
            `Level(s) ${(m.params as { id?: string }).id ?? ''}`,
        } satisfies BreadcrumbHandle,
      },
      // W74-F3-S2 — LCA emission-factor proposal review + manual override.
      {
        path: '/lca-projects/:id/factors',
        element: <LcaFactorsPage />,
      },
      // W71-F4-S2 — Scope-2 market-based emissions proof report (read-only).
      {
        path: '/eacs/scope-2-report',
        element: <EacScope2ProofReportPage />,
        handle: {
          breadcrumb: 'eacScope2.title',
        } satisfies BreadcrumbHandle,
      },
      // W6-F1 — tasks kanban + task detail over Core service-issues.
      {
        path: '/buildings/:buildingId/tasks',
        element: <BuildingTasksPage />,
      },
      {
        path: '/buildings/:buildingId/tasks/:taskId',
        element: <BuildingTaskDetailPage />,
      },
      // W65-F4-S2 (#352) — partner-admin amenity management + bookings.
      {
        path: '/buildings/:buildingId/amenities',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <AmenitiesAdminPage />
          </RequireRole>
        ),
        handle: {
          breadcrumb: 'buildings.amenities_admin.heading',
        } satisfies BreadcrumbHandle,
      },
      // W6-F4 — notification center + preferences.
      { path: '/notifications', element: <NotificationCenterPage /> },
      {
        path: '/settings/notifications',
        element: <NotificationPreferencesPage />,
      },
      // W8 Analytics surface (F1).
      { path: '/analytics', element: <AnalyticsListPage /> },
      // W8 Editor (F2) — `/new` and `/:id` variants. 3-segment paths so they
      // don't conflict with `/analytics/:dashboardId`.
      { path: '/analytics/editor/new', element: <AnalyticsEditorPage /> },
      { path: '/analytics/editor/:dashboardId', element: <AnalyticsEditorPage /> },
      // W8 Report builder (F3) — must precede `:dashboardId` so it wins the match.
      { path: '/analytics/reports', element: <ReportBuilderPage /> },
      // W8 Scheduled reports (F4-S3) — must also precede `:dashboardId`.
      { path: '/analytics/scheduled', element: <ScheduledReportsPage /> },
      // W124-F4-S1b — benchmarking dashboard — also precedes `:dashboardId`.
      { path: '/analytics/benchmarking', element: <BenchmarkingPage /> },
      // W8 Per-building drill-down (F1-S3) — must also precede `:dashboardId`.
      { path: '/analytics/buildings/:buildingId', element: <AnalyticsBuildingPage /> },
      { path: '/analytics/:dashboardId', element: <AnalyticsDashboardPage /> },
      // Placeholder routes — pages land in later waves.
      // W15-F1-S2 — Cross-domain report builder
      { path: '/reports/builder', element: <QueryBuilderPage /> },
      // W15-F1-S4 — Cross-domain report examples gallery
      { path: '/reports/examples', element: <ExamplesGalleryPage /> },
      // W15-F2-S1 — Saved cross-domain queries
      { path: '/reports/saved', element: <SavedQueriesPage /> },
      { path: '/reports/saved/:id', element: <SavedQueryDetailPage /> },
      // W15-F3-S1 — Executive one-screen dashboard
      { path: '/dashboards/executive', element: <ExecutiveDashboardPage /> },
      // W134-F6-S2 — Carbon accounting dashboard
      { path: '/dashboards/carbon', element: <CarbonDashboardPage /> },
      { path: '/reports', element: <ReportsIndexPage /> },
      // W5-F5-S2 — partner onboarding wizard
      { path: '/onboarding/partner', element: <PartnerOnboardingWizard /> },
      // W5-F5-S3 — IdP self-service registration
      // #892 P0-006 followup: gated to admin roles; FE routes now mirror
      // the NavGroup admin filter so hand-pasted URLs 403 instead of
      // rendering the page chrome before the API rejects the mutation.
      {
        path: '/admin/settings/identity',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <IdentityProviderSettingsPage />
          </RequireRole>
        ),
      },
      // W11-F9-S1 — partner API-key management. #892 P0-006 followup gate.
      {
        path: '/admin/api-keys',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <ApiKeysPage />
          </RequireRole>
        ),
      },
      // W53-F3-S2 — NL-query pipeline probe (partner_admin / platform_admin).
      {
        path: '/admin/nl-query-probe',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <NlQueryProbePage />
          </RequireRole>
        ),
        handle: { breadcrumb: 'NL-query probe' } satisfies BreadcrumbHandle,
      },
      // W85-F4 (Phase 15) — Pilot SLA dashboard (platform_admin only).
      {
        path: '/admin/sre/pilot-sla',
        element: (
          <RequireRole
            roles={['platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <PilotSlaDashboardPage />
          </RequireRole>
        ),
        handle: { breadcrumb: 'Pilot SLA dashboard' } satisfies BreadcrumbHandle,
      },
      // W51-F3-S2 — per-customer ETL migration console (platform_admin only).
      {
        path: '/admin/migrations',
        element: (
          <RequireRole
            roles={['platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <MigrationsPage />
          </RequireRole>
        ),
        handle: { breadcrumb: 'Customer migrations' } satisfies BreadcrumbHandle,
      },
      // W51-F4-S2 — per-customer migration-readiness dashboard (platform_admin
      // only). Static segment is declared before `:runId` so it matches first.
      {
        path: '/admin/migrations/readiness',
        element: (
          <RequireRole
            roles={['platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <ReadinessDashboard />
          </RequireRole>
        ),
        handle: { breadcrumb: 'Migration readiness' } satisfies BreadcrumbHandle,
      },
      // W51-F3-S2 — single migration-run detail (platform_admin only).
      {
        path: '/admin/migrations/:runId',
        element: (
          <RequireRole
            roles={['platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <MigrationDetailPage />
          </RequireRole>
        ),
        handle: { breadcrumb: 'Migration run' } satisfies BreadcrumbHandle,
      },
      // W20-F1-S1 — partner-admin modules list (sortable + visibility toggle).
      // Gated to `partner_admin`/`platform_admin`; other roles see 403.
      {
        path: '/admin/modules',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <ModulesPage />
          </RequireRole>
        ),
        handle: { breadcrumb: 'admin.modules.nav' } satisfies BreadcrumbHandle,
      },
      // W57-F2-S1 — "New module from text" authoring page (partner_admin only).
      // Paste regulation text → AI proposes a module draft → refine / discard.
      {
        path: '/admin/modules/new-from-text',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <Suspense fallback={<PageSuspenseFallback />}>
              <NewModuleFromTextPage />
            </Suspense>
          </RequireRole>
        ),
        handle: {
          breadcrumb: 'admin.newModuleFromText.nav',
        } satisfies BreadcrumbHandle,
      },
      // W47-F5-S1 — read-only subscription + entitlements view.
      // Gated to admin roles so the upgrade CTA only appears for users
      // who can act on it.
      {
        path: '/admin/subscription',
        element: (
          <RequireRole
            roles={['partner_admin', 'customer_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <Suspense fallback={<PageSuspenseFallback />}>
              <SubscriptionPage />
            </Suspense>
          </RequireRole>
        ),
        handle: { breadcrumb: 'admin.subscription.nav' } satisfies BreadcrumbHandle,
      },
      // W20-F2-S1 — module edit (4 tabs).
      {
        path: '/admin/modules/:id/edit',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <ModuleEditPage />
          </RequireRole>
        ),
        handle: { breadcrumb: 'admin.moduleEdit.breadcrumbs.edit' } satisfies BreadcrumbHandle,
      },
      // W20-F3-S3 — module-template authoring (partner_admin only).
      // Stub bodies in S3; real bodies in S4 (list), S5 (editor shell),
      // S6-S8 (category / chapter / variable + options). Uses Core's
      // template -> category -> chapter -> variable -> option nesting,
      // not the legacy CakePHP "chapter contains category" shape.
      {
        path: '/admin/templates',
        element: (
          <RequireRole
            roles={['partner_admin', 'manager', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <Suspense fallback={<PageSuspenseFallback />}>
              <ModuleTemplatesPage />
            </Suspense>
          </RequireRole>
        ),
        handle: {
          breadcrumb: 'admin.moduleTemplates.breadcrumbs.list',
        } satisfies BreadcrumbHandle,
      },
      {
        path: '/admin/templates/:id',
        element: (
          <RequireRole
            roles={['partner_admin', 'manager', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <Suspense fallback={<PageSuspenseFallback />}>
              <ModuleTemplateEditorPage />
            </Suspense>
          </RequireRole>
        ),
        handle: {
          breadcrumb: 'admin.moduleTemplates.breadcrumbs.editor',
        } satisfies BreadcrumbHandle,
      },
      // W82-F2 — standalone partner-admin preview pane. Renders the
      // draft template as if a customer were evaluating it; live
      // scoring updates as the previewer answers variables.
      {
        path: '/admin/templates/:id/preview',
        element: (
          <RequireRole
            roles={['partner_admin', 'manager', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <Suspense fallback={<PageSuspenseFallback />}>
              <ModuleTemplatePreviewPage />
            </Suspense>
          </RequireRole>
        ),
        handle: {
          breadcrumb: 'admin.templates.preview.title',
        } satisfies BreadcrumbHandle,
      },
      // #1708 — BREEAM templates is no longer a separate surface; it is the
      // module-templates list (`/admin/templates`) viewed through the BREEAM
      // framework lens. The old route redirects so bookmarks / deep-links
      // keep working and land directly in the matrix view.
      {
        path: '/admin/breeam-templates',
        element: <Navigate to="/admin/templates?framework=breeam" replace />,
      },
      // W20-F3-S11 — version history page (partner_admin / platform_admin
      // can migrate; customer_admin sees the read-only variant via
      // /templates/available/:id/versions below).
      {
        path: '/admin/templates/:id/versions',
        element: (
          <RequireRole
            roles={['partner_admin', 'manager', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <Suspense fallback={<PageSuspenseFallback />}>
              <ModuleTemplateVersionsPage />
            </Suspense>
          </RequireRole>
        ),
        handle: {
          breadcrumb: 'admin.templateVersions.tabLabel',
        } satisfies BreadcrumbHandle,
      },
      // W20-F5-S2 — variable areas admin (catalog grouping).
      {
        path: '/admin/variable-areas',
        element: (
          <RequireRole
            roles={['partner_admin', 'manager', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <Suspense fallback={<PageSuspenseFallback />}>
              <VariableAreasPage />
            </Suspense>
          </RequireRole>
        ),
        handle: {
          breadcrumb: 'admin.variableAreas.breadcrumbs.list',
        } satisfies BreadcrumbHandle,
      },
      // W20-F5-S3 — recurring-variables catalog with live preview.
      {
        path: '/admin/recurring-variables',
        element: (
          <RequireRole
            roles={['partner_admin', 'manager', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <Suspense fallback={<PageSuspenseFallback />}>
              <RecurringVariablesPage />
            </Suspense>
          </RequireRole>
        ),
        handle: {
          breadcrumb: 'admin.recurringVariables.breadcrumbs.list',
        } satisfies BreadcrumbHandle,
      },
      {
        path: '/templates/available/:id/versions',
        element: (
          <RequireRole
            roles={['customer_admin', 'partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <Suspense fallback={<PageSuspenseFallback />}>
              <ModuleTemplateVersionsPage readOnly />
            </Suspense>
          </RequireRole>
        ),
        handle: {
          breadcrumb: 'admin.templateVersions.tabLabel',
        } satisfies BreadcrumbHandle,
      },
      // W20-F3-S3 — read-only "Templates available in this org" view
      // for customer_admin (partner_admin / platform_admin can also
      // see it but the editable surface above is the primary entry).
      {
        path: '/templates/available',
        element: (
          <RequireRole
            roles={['customer_admin', 'partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <Suspense fallback={<PageSuspenseFallback />}>
              <TemplatesAvailablePage />
            </Suspense>
          </RequireRole>
        ),
        handle: {
          breadcrumb: 'admin.moduleTemplates.breadcrumbs.available',
        } satisfies BreadcrumbHandle,
      },
      // W16-F3-S1 — admin landing page (11-card grid).
      // #892 P0-006 followup: gate the landing page to admin roles
      // (matches NavGroup visibility) so non-admins land on 403 instead
      // of an empty card grid.
      {
        path: '/admin',
        element: (
          <RequireRole
            roles={['partner_admin', 'customer_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <AdminLandingPage />
          </RequireRole>
        ),
      },
      // W11-F9-S2 — outbound webhook subscriptions admin (legacy URL,
      // kept for back-compat alongside the v2-shape page below).
      // #892 P0-006 followup gate.
      {
        path: '/admin/webhook-subscriptions',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <WebhookSubscriptionsPage />
          </RequireRole>
        ),
      },
      // W20-F7-S2 — v2-shape webhooks admin shell at `/admin/webhooks`.
      // Gated to partner_admin / platform_admin per F7 spec; W20-F7-S3
      // populates the list / create / edit UX inside this shell.
      {
        path: '/admin/webhooks',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <WebhooksPage />
          </RequireRole>
        ),
      },
      // W10-F3-S2 — user settings (profile / preferences / language).
      { path: '/settings', element: <SettingsPage /> },
      // W143-F2-S1 — CustomerAdmin self-service connector view.
      { path: '/settings/connectors', element: <ConnectorsSettingsPage /> },
      // W9-F2-S2 — admin webhook replay UI (RBAC enforced server-side).
      // #892 P0-006 followup: add FE gate matching server enforcement.
      {
        path: '/admin/integrations/webhooks',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <WebhookAdminPage />
          </RequireRole>
        ),
      },
      // W73-F4-S2 — carrier-API admin connection-management UX. The static
      // `/setup` + `/conflicts` segments are declared before `/:id` so they
      // match ahead of the dynamic detail segment.
      {
        path: '/admin/integrations/carrier-apis',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <CarrierApiConnectionsPage />
          </RequireRole>
        ),
        handle: { breadcrumb: 'Carrier APIs' } satisfies BreadcrumbHandle,
      },
      {
        path: '/admin/integrations/carrier-apis/setup',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <CarrierApiSetupPage />
          </RequireRole>
        ),
        handle: { breadcrumb: 'Set up carrier' } satisfies BreadcrumbHandle,
      },
      {
        path: '/admin/integrations/carrier-apis/conflicts',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <CarrierSyncConflictsPage />
          </RequireRole>
        ),
        handle: { breadcrumb: 'Sync conflicts' } satisfies BreadcrumbHandle,
      },
      {
        path: '/admin/integrations/carrier-apis/:id',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <CarrierApiConnectionDetailPage />
          </RequireRole>
        ),
        handle: { breadcrumb: 'Carrier connection' } satisfies BreadcrumbHandle,
      },
      // W72-F1-S2 — bank-account linkage UX. The static `/link` segment is
      // declared before `/:id` so it matches ahead of the dynamic detail
      // segment. The org is resolved from the active-partner switcher (the
      // `X-Org-Id` header `apiFetch` stamps), so these routes intentionally
      // mirror the carrier-API admin tenancy model rather than carrying the
      // org in the path.
      {
        path: '/admin/integrations/banking',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <BankAccountsPage />
          </RequireRole>
        ),
        handle: { breadcrumb: 'Bank accounts' } satisfies BreadcrumbHandle,
      },
      {
        path: '/admin/integrations/banking/link',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <BankingLinkPage />
          </RequireRole>
        ),
        handle: { breadcrumb: 'Link a bank account' } satisfies BreadcrumbHandle,
      },
      {
        path: '/admin/integrations/banking/:id',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <BankAccountDetailPage />
          </RequireRole>
        ),
        handle: { breadcrumb: 'Bank connection' } satisfies BreadcrumbHandle,
      },
      // W10-F1-S2 — admin translations editor.
      // #892 P0-006 followup gate.
      {
        path: '/admin/translations',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <TranslationsPage />
          </RequireRole>
        ),
      },
      // W22-F2-S2 — admin Languages registry (CRUD + completion %).
      // #892 P0-006 followup gate.
      {
        path: '/admin/languages',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <AdminLanguagesPage />
          </RequireRole>
        ),
      },
      // #1545 — admin Currencies catalog (CRUD + setActive guard on default).
      {
        path: '/admin/currencies',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <AdminCurrenciesPage />
          </RequireRole>
        ),
      },
      // #1546 — admin Measurement Units catalog.
      {
        path: '/admin/measurement-units',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <AdminMeasurementUnitsPage />
          </RequireRole>
        ),
      },
      // #1547 — admin Tags catalog.
      {
        path: '/admin/tags',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <AdminTagsPage />
          </RequireRole>
        ),
      },
      // #1550 — global time-travel override (platform-only).
      {
        path: '/admin/system/time-travel',
        element: (
          <RequireRole
            roles={['platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <AdminTimeTravelPage />
          </RequireRole>
        ),
      },
      // #1548 — EU Taxonomy per-country benchmarks editor (platform-only).
      {
        path: '/admin/eu-taxonomy/benchmarks',
        element: (
          <RequireRole
            roles={['platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <AdminEuTaxonomyBenchmarksPage />
          </RequireRole>
        ),
      },
      // #1549 — EU Taxonomy XLS regression harness (platform-only).
      {
        path: '/admin/test-runs/eu-taxonomy',
        element: (
          <RequireRole
            roles={['platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <AdminEuTaxonomyTestRunsPage />
          </RequireRole>
        ),
      },
      // W22-F4-S1 — partner branding editor.
      {
        path: '/admin/branding',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <Suspense fallback={<PageSuspenseFallback />}>
              <BrandingPage />
            </Suspense>
          </RequireRole>
        ),
        handle: { breadcrumb: 'branding.breadcrumbs.editor' } satisfies BreadcrumbHandle,
      },
      // W22-F9-S1 — partner CRUD admin (partner_admin / platform_admin).
      {
        path: '/admin/partners',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <Suspense fallback={<PageSuspenseFallback />}>
              <AdminPartnersPage />
            </Suspense>
          </RequireRole>
        ),
      },
      // #1674 (Stage 0 of #1665 parity rebuild) — partner edit-shell.
      // Multi-tab edit page; the existing `<PartnerEditDialog>` stays
      // for the create flow.
      {
        path: '/admin/partners/:id',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <Suspense fallback={<PageSuspenseFallback />}>
              <PartnerEditPage />
            </Suspense>
          </RequireRole>
        ),
      },
      // W10-F4 — partner settings stub (real implementation lands with W5).
      // #892 P0-006 followup gate.
      {
        path: '/admin/partner-settings',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <PartnerSettingsPage />
          </RequireRole>
        ),
      },
      // W47-F7-S1 — customer-facing /support page (visible to all authed users).
      { path: '/support', element: <CustomerSupportPage /> },
      // #1533 A4 — /help index (docs + FAQ + shortcuts + contact).
      { path: '/help', element: <HelpIndexPage /> },
      {
        path: '/support/tickets/:id',
        element: <CustomerSupportTicketDetailPage />,
      },
      // W10-F5-S4 — admin support-ticket triage UI.
      // #892 P0-006 followup: gate to admin roles. The customer-facing
      // ticket detail at /support/tickets/:id remains open to authors.
      {
        path: '/admin/support/tickets',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <SupportTicketsListPage />
          </RequireRole>
        ),
      },
      {
        path: '/admin/support/tickets/:id',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <SupportTicketDetailPage />
          </RequireRole>
        ),
      },
      // W10-F6-S1 — system config (per-org feature flags).
      // #892 P0-006 followup: most sensitive admin surface — restrict
      // to platform_admin only.
      {
        path: '/admin/system',
        element: (
          <RequireRole
            roles={['platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <SystemConfigPage />
          </RequireRole>
        ),
      },
      // W17-F2-S5 + W22-F5-S2 — audit-log viewer (admin-only).
      // Server-side enforces tenancy; client-side `<RequireRole>` ensures
      // the page never renders for non-admins (clearer UX than a blank
      // server-403 response).
      {
        path: '/admin/logs',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <GlobalAuditLogPage />
          </RequireRole>
        ),
      },
      // W22-F5-S3 — per-asset audit log scope. Asset / portfolio owners
      // (non-admin) can also reach this view if W17-F2 authorises it
      // server-side; `<RequireRole>` is intentionally absent so the
      // server response shapes the experience.
      {
        path: '/admin/logs/asset/:assetId',
        element: <AssetAuditLogPage />,
      },
      {
        path: '/admin/logs/user/:userId',
        element: <UserAuditLogPage />,
      },
      // W56-F4-S2 (#268) — evidence-classification audit surface. Client
      // role-gates to admins for clearer UX; the server `AuditController`
      // remains the source of truth (tenancy + role narrowing).
      {
        path: '/admin/audit/evidence-classifications',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <EvidenceClassificationAuditPage />
          </RequireRole>
        ),
      },
      // W10-F6-S2..S4 — bulk import wizard + connectors + job progress.
      // Order matters: more specific (`/new`, `/connectors`) BEFORE `:id`.
      // #892 P0-006 followup: all 4 import routes gate to admin roles —
      // import mutations write across many orgs, never expose to client.
      {
        path: '/admin/imports',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <ImportsListPage />
          </RequireRole>
        ),
      },
      {
        path: '/admin/imports/new',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <ImportWizardPage />
          </RequireRole>
        ),
      },
      {
        path: '/admin/imports/connectors',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <ConnectorsListPage />
          </RequireRole>
        ),
      },
      {
        path: '/admin/imports/:id',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <ImportJobDetailPage />
          </RequireRole>
        ),
      },
      // W21-F2-S2 — user lifecycle admin (4 views under shared layout).
      // #892 P0-006 followup: wrap the layout in RequireRole so every
      // child child (pending / non-validated / authorized / blocked)
      // inherits the gate. User-management mutations span tenants,
      // never expose to client roles.
      {
        path: '/admin/users',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <UsersAdminLayout />
          </RequireRole>
        ),
        children: [
          { index: true, element: <Navigate to="pending" replace /> },
          { path: 'pending', element: <PendingUsersPage /> },
          { path: 'non-validated', element: <NonValidatedUsersPage /> },
          { path: 'authorized', element: <AuthorizedUsersPage /> },
          { path: 'blocked', element: <BlockedUsersPage /> },
        ],
      },
      // Senviro #1718 — admin "edit another user" parity port.
      // Lives outside the lifecycle layout so its own back-link routes
      // back to /admin/users without re-mounting the tab strip.
      {
        path: '/admin/users/:coreUserId/edit',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <AdminUserEditPage />
          </RequireRole>
        ),
      },
      // W21-F2-S3 — invitation accept-landing
      { path: '/auth/accept-invitation', element: <AcceptInvitationPage /> },
      // W19-F1-S2 — top-level cross-framework modules listing.
      {
        path: '/modules',
        element: (
          <Suspense fallback={<PageSuspenseFallback />}>
            <ModulesListPage />
          </Suspense>
        ),
        handle: { breadcrumb: 'modules.list.title' } satisfies BreadcrumbHandle,
      },
      // #1425 / W39-F4-S1 — assessor-assignment workflow. The static
      // my-queue path MUST come before the `:assignmentId` dynamic route
      // so react-router matches it first. Route-only (no nav item yet).
      {
        path: '/assessor-assignments',
        element: (
          <RequireRole
            roles={[
              'manager',
              'consultant',
              'partner_admin',
              'platform_admin',
            ]}
            fallback={<ForbiddenPage />}
          >
            <Suspense fallback={<PageSuspenseFallback />}>
              <AssessorQueuePage />
            </Suspense>
          </RequireRole>
        ),
        handle: {
          breadcrumb: 'assessorWorkflow.queue.heading',
        } satisfies BreadcrumbHandle,
      },
      {
        path: '/assessor-assignments/:assignmentId',
        element: (
          <RequireRole
            roles={[
              'manager',
              'consultant',
              'partner_admin',
              'platform_admin',
            ]}
            fallback={<ForbiddenPage />}
          >
            <Suspense fallback={<PageSuspenseFallback />}>
              <AssessorAssignmentDetailPage />
            </Suspense>
          </RequireRole>
        ),
        handle: {
          breadcrumb: 'assessorWorkflow.detail.breadcrumb',
        } satisfies BreadcrumbHandle,
      },
      // W77-F1 / W77-F2 — Phase 14 acquisitions pipeline + deal detail.
      {
        path: '/acquisitions',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <Suspense fallback={<PageSuspenseFallback />}>
              <AcquisitionsPipelinePage />
            </Suspense>
          </RequireRole>
        ),
        handle: {
          breadcrumb: 'acquisitions.pipeline.heading',
        } satisfies BreadcrumbHandle,
      },
      // W77-F3-S2 — org-wide counterparty roster. Static path must come
      // before the `/acquisitions/:dealId` dynamic route so react-router
      // matches it first.
      {
        path: '/acquisitions/counterparties',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <Suspense fallback={<PageSuspenseFallback />}>
              <CounterpartiesRosterPage />
            </Suspense>
          </RequireRole>
        ),
        handle: {
          breadcrumb: 'acquisitions.counterparties.rosterHeading',
        } satisfies BreadcrumbHandle,
      },
      // W88-F2 — Phase 15 multi-step deal create wizard. MUST come
      // BEFORE the `/acquisitions/:dealId` dynamic route or react-router
      // will treat "new" as a deal id.
      {
        path: '/acquisitions/new',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <Suspense fallback={<PageSuspenseFallback />}>
              <DealCreateWizard />
            </Suspense>
          </RequireRole>
        ),
        handle: {
          breadcrumb: 'acquisitions.wizard.heading',
        } satisfies BreadcrumbHandle,
      },
      {
        path: '/acquisitions/:dealId',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <Suspense fallback={<PageSuspenseFallback />}>
              <DealDetailPage />
            </Suspense>
          </RequireRole>
        ),
        handle: {
          breadcrumb: (m) =>
            `Deal ${(m.params as { dealId?: string }).dealId ?? ''}`,
        } satisfies BreadcrumbHandle,
      },
      // W73-F2-S2 — insurance claims tracking (Phase 13). The `/new` create
      // route must register BEFORE the dynamic `:claimId`-bearing detail/edit
      // routes so react-router never treats "new" as a claim id.
      {
        path: '/policies/:policyId/claims/new',
        element: (
          <RequireRole
            roles={['manager', 'consultant', 'partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <Suspense fallback={<PageSuspenseFallback />}>
              <ClaimFormPage />
            </Suspense>
          </RequireRole>
        ),
        handle: {
          breadcrumb: 'insuranceClaims.form.createHeading',
        } satisfies BreadcrumbHandle,
      },
      {
        path: '/policies/:policyId/claims',
        element: (
          <Suspense fallback={<PageSuspenseFallback />}>
            <PolicyClaimsPage />
          </Suspense>
        ),
        handle: {
          breadcrumb: 'insuranceClaims.policyList.heading',
        } satisfies BreadcrumbHandle,
      },
      {
        path: '/assets/:assetId/claim-history',
        element: (
          <Suspense fallback={<PageSuspenseFallback />}>
            <AssetClaimHistoryPage />
          </Suspense>
        ),
        handle: {
          breadcrumb: 'insuranceClaims.assetHistory.heading',
        } satisfies BreadcrumbHandle,
      },
      {
        path: '/claims/:claimId/edit',
        element: (
          <RequireRole
            roles={['manager', 'consultant', 'partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <Suspense fallback={<PageSuspenseFallback />}>
              <ClaimFormPage />
            </Suspense>
          </RequireRole>
        ),
        handle: {
          breadcrumb: 'insuranceClaims.form.editHeading',
        } satisfies BreadcrumbHandle,
      },
      {
        path: '/claims/:claimId',
        element: (
          <Suspense fallback={<PageSuspenseFallback />}>
            <ClaimDetailPage />
          </Suspense>
        ),
        handle: {
          breadcrumb: 'insuranceClaims.detail.back',
        } satisfies BreadcrumbHandle,
      },
      // W60-F5-S2 (#304) — workspace-level valuation index. The per-deal
      // tabs + per-run pages shipped under W77; this is the standalone
      // list that the sidebar links to. Declared before `/valuation/:runId`
      // so the exact `/valuation` path is not captured by the param route.
      {
        path: '/valuation',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <Suspense fallback={<PageSuspenseFallback />}>
              <ValuationListPage />
            </Suspense>
          </RequireRole>
        ),
        handle: {
          breadcrumb: 'valuation.list.title',
        } satisfies BreadcrumbHandle,
      },
      // W77 PR C — standalone valuation-run surfaces. Routes register
      // here so an operator can deep-link to a run (or its scenarios)
      // without having to navigate through the deal-detail page.
      {
        path: '/valuation/:runId/scenarios',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <Suspense fallback={<PageSuspenseFallback />}>
              <ValuationScenariosPage />
            </Suspense>
          </RequireRole>
        ),
        handle: {
          breadcrumb: 'valuation.scenarios.tab.title',
        } satisfies BreadcrumbHandle,
      },
      {
        path: '/valuation/:runId',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <Suspense fallback={<PageSuspenseFallback />}>
              <ValuationRunPage />
            </Suspense>
          </RequireRole>
        ),
        handle: {
          breadcrumb: (m) =>
            `Run ${(m.params as { runId?: string }).runId ?? ''}`,
        } satisfies BreadcrumbHandle,
      },
      // W77 PR D — Phase 14 dispositions pipeline + transaction-room.
      {
        path: '/dispositions',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <Suspense fallback={<PageSuspenseFallback />}>
              <DispositionsListPage />
            </Suspense>
          </RequireRole>
        ),
        handle: {
          breadcrumb: 'dispositions.pipeline.heading',
        } satisfies BreadcrumbHandle,
      },
      // W88-F3 — Phase 15 multi-step disposition create wizard. MUST
      // come BEFORE the `/dispositions/:dispositionId` dynamic route.
      {
        path: '/dispositions/new',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <Suspense fallback={<PageSuspenseFallback />}>
              <DispositionCreateWizard />
            </Suspense>
          </RequireRole>
        ),
        handle: {
          breadcrumb: 'dispositions.wizard.heading',
        } satisfies BreadcrumbHandle,
      },
      {
        path: '/dispositions/:dispositionId',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <Suspense fallback={<PageSuspenseFallback />}>
              <DispositionDetailPage />
            </Suspense>
          </RequireRole>
        ),
        handle: {
          breadcrumb: (m) =>
            `Disposition ${(m.params as { dispositionId?: string }).dispositionId ?? ''}`,
        } satisfies BreadcrumbHandle,
      },
      // W63-F1-S1 — disposition sale-prep workspace. The singular
      // `/disposition/*` prefix is distinct from the W77 `/dispositions`
      // pipeline above; both can coexist. Detail route comes after the
      // list route (no dynamic-segment collision since the list path is
      // static `/disposition/engagements`).
      {
        path: '/disposition/engagements',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <Suspense fallback={<PageSuspenseFallback />}>
              <EngagementListPage />
            </Suspense>
          </RequireRole>
        ),
        handle: {
          breadcrumb: 'disposition.list.heading',
        } satisfies BreadcrumbHandle,
      },
      {
        path: '/disposition/engagements/:engagementId',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <Suspense fallback={<PageSuspenseFallback />}>
              <EngagementDetailPage />
            </Suspense>
          </RequireRole>
        ),
        handle: {
          breadcrumb: (m) =>
            `Engagement ${(m.params as { engagementId?: string }).engagementId ?? ''}`,
        } satisfies BreadcrumbHandle,
      },
      // W67-F2 — compliance calendar (month / quarter / year views).
      // Partner-admin only; client-side role gate matches the server
      // tenancy guard for a cleaner 403 experience.
      {
        path: '/compliance/calendar',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <Suspense fallback={<PageSuspenseFallback />}>
              <ComplianceCalendarPage />
            </Suspense>
          </RequireRole>
        ),
        handle: {
          breadcrumb: 'compliance.calendar.heading',
        } satisfies BreadcrumbHandle,
      },
      // W137-F6-S2 — AI compliance assistant chat.
      {
        path: '/compliance/assistant',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <Suspense fallback={<PageSuspenseFallback />}>
              <ComplianceAssistantPage />
            </Suspense>
          </RequireRole>
        ),
        handle: {
          breadcrumb: 'compliance.assistant.heading',
        } satisfies BreadcrumbHandle,
      },
      // W56-F2-S1 — evidence classification inbox.
      {
        path: '/evidence/inbox',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <Suspense fallback={<PageSuspenseFallback />}>
              <EvidenceInboxPage />
            </Suspense>
          </RequireRole>
        ),
        handle: {
          breadcrumb: 'evidence.inbox.heading',
        } satisfies BreadcrumbHandle,
      },
      // W56-F2-S2 — auto-applied evidence classification audit (tenant admins).
      {
        path: '/evidence/auto-applied',
        element: (
          <RequireRole
            roles={['customer_admin', 'partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <Suspense fallback={<PageSuspenseFallback />}>
              <AutoAppliedAuditPage />
            </Suspense>
          </RequireRole>
        ),
        handle: {
          breadcrumb: 'evidence.autoApplied.heading',
        } satisfies BreadcrumbHandle,
      },
      // W56-F3-S2 — partner-admin bulk reclassify (preview-then-apply).
      {
        path: '/admin/evidence/reclassify-bulk',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <Suspense fallback={<PageSuspenseFallback />}>
              <BulkReclassificationPage />
            </Suspense>
          </RequireRole>
        ),
        handle: {
          breadcrumb: 'admin.reclassifyBulk.heading',
        } satisfies BreadcrumbHandle,
      },
      // W67-F3-S2 — admin defaults for the compliance calendar.
      {
        path: '/admin/compliance-calendar',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <Suspense fallback={<PageSuspenseFallback />}>
              <CalendarDefaultsPage />
            </Suspense>
          </RequireRole>
        ),
        handle: {
          breadcrumb: 'adminCompliance.calendar_defaults.heading',
        } satisfies BreadcrumbHandle,
      },
      // W66-F4-S1 — workflows list.
      {
        path: '/workflows',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <Suspense fallback={<PageSuspenseFallback />}>
              <WorkflowsListPage />
            </Suspense>
          </RequireRole>
        ),
        handle: {
          breadcrumb: 'workflows.list.title',
        } satisfies BreadcrumbHandle,
      },
      // W86-F4 — partner-admin tenant-requests console (Phase 15).
      // The backend at `GET /api/v1/tenant-requests` is principal-scoped:
      // partner-admins get the org-wide list; tenant-tier callers only
      // see their own. We gate the route to partner_admin / platform_admin
      // and rely on the server tenancy guard as the authoritative check.
      {
        path: '/admin/tenant-requests',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <Suspense fallback={<PageSuspenseFallback />}>
              <TenantRequestsAdminPage />
            </Suspense>
          </RequireRole>
        ),
        handle: {
          breadcrumb: 'tenantRequestsAdmin.heading',
        } satisfies BreadcrumbHandle,
      },
      // W78 — Construction + Development FE (Phase 14). `/projects` lists
      // every project in the org; `/projects/:projectId` opens the 6-tab
      // detail page. Partner-admin / platform-admin only — the backend
      // tenancy guard at `apps/api/src/projects/projects.controller.ts`
      // remains the authoritative gate.
      {
        path: '/projects',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <Suspense fallback={<PageSuspenseFallback />}>
              <ProjectsListPage />
            </Suspense>
          </RequireRole>
        ),
        handle: {
          breadcrumb: 'construction.projects.heading',
        } satisfies BreadcrumbHandle,
      },
      // W88-F1 — Phase 15 multi-step project create wizard. MUST come
      // BEFORE the `/projects/:projectId` dynamic route.
      {
        path: '/projects/new',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <Suspense fallback={<PageSuspenseFallback />}>
              <ProjectCreateWizard />
            </Suspense>
          </RequireRole>
        ),
        handle: {
          breadcrumb: 'construction.wizard.heading',
        } satisfies BreadcrumbHandle,
      },
      {
        path: '/projects/:projectId',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <Suspense fallback={<PageSuspenseFallback />}>
              <ProjectDetailPage />
            </Suspense>
          </RequireRole>
        ),
        handle: {
          breadcrumb: (m) =>
            `Project ${(m.params as { projectId?: string }).projectId ?? ''}`,
        } satisfies BreadcrumbHandle,
      },
      // W79-F1 — module marketplace browse + detail (Phase 14). All
      // authenticated users can browse; listing detail surfaces the
      // install CTA + reviews. Static `/marketplace/payouts*` routes are
      // declared BEFORE the `/marketplace/listings/:listingId` dynamic
      // route so react-router matches them first.
      {
        path: '/marketplace',
        element: (
          <Suspense fallback={<PageSuspenseFallback />}>
            <MarketplaceBrowsePage />
          </Suspense>
        ),
        handle: {
          breadcrumb: 'marketplace.browse.title',
        } satisfies BreadcrumbHandle,
      },
      // W79-F5 — author payouts dashboard + Stripe Connect onboarding.
      // Author-scoped; the backend filters by tenant — any authenticated
      // org sees its own ledger (empty if not an author yet).
      {
        path: '/marketplace/payouts',
        element: (
          <Suspense fallback={<PageSuspenseFallback />}>
            <MarketplacePayoutsPage />
          </Suspense>
        ),
        handle: {
          breadcrumb: 'marketplace.payouts.title',
        } satisfies BreadcrumbHandle,
      },
      // W64-F2-S2 (#337) — author-side publish/submit surfaces. Static
      // segments declared before `/marketplace/listings/:listingId` so they
      // win the route match. Author-scoped; the backend enforces tenant +
      // the marketplace publish role.
      {
        path: '/marketplace/my-listings',
        element: (
          <Suspense fallback={<PageSuspenseFallback />}>
            <MyListingsPage />
          </Suspense>
        ),
        handle: {
          breadcrumb: 'marketplace.myListings.title',
        } satisfies BreadcrumbHandle,
      },
      {
        path: '/marketplace/publish/:templateId',
        element: (
          <Suspense fallback={<PageSuspenseFallback />}>
            <PublishListingPage />
          </Suspense>
        ),
        handle: {
          breadcrumb: 'marketplace.publish.title',
        } satisfies BreadcrumbHandle,
      },
      {
        path: '/marketplace/listings/:listingId',
        element: (
          <Suspense fallback={<PageSuspenseFallback />}>
            <MarketplaceListingDetailPage />
          </Suspense>
        ),
        handle: {
          breadcrumb: 'marketplace.detail.breadcrumb',
        } satisfies BreadcrumbHandle,
      },
      // W79-F2 — platform_admin curator queue. Server enforces the role
      // gate; client-side `<RequireRole>` keeps the 403 experience
      // consistent with the rest of the admin shell.
      {
        path: '/admin/marketplace/queue',
        element: (
          <RequireRole
            roles={['platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <Suspense fallback={<PageSuspenseFallback />}>
              <MarketplaceCuratorQueuePage />
            </Suspense>
          </RequireRole>
        ),
        handle: {
          breadcrumb: 'marketplace.curator.title',
        } satisfies BreadcrumbHandle,
      },
      // W79-F3 — per-org install pipeline.
      {
        path: '/orgs/me/installs',
        element: (
          <Suspense fallback={<PageSuspenseFallback />}>
            <MarketplaceInstallsPage />
          </Suspense>
        ),
        handle: {
          breadcrumb: 'marketplace.installs.title',
        } satisfies BreadcrumbHandle,
      },
      // W66-F4-S2 — workflow visual builder. `:workflowId?` lets the page
      // open in fresh-draft mode (no param) or edit mode (existing id).
      {
        path: '/workflows/builder/:workflowId?',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <Suspense fallback={<PageSuspenseFallback />}>
              <WorkflowBuilderPage />
            </Suspense>
          </RequireRole>
        ),
        handle: {
          breadcrumb: 'workflows.builder.title',
        } satisfies BreadcrumbHandle,
      },
      // W66-F5-S2 — run history for a single workflow. Declared with the
      // static `runs` suffix so it never collides with `/workflows/builder/*`.
      {
        path: '/workflows/:workflowId/runs',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <Suspense fallback={<PageSuspenseFallback />}>
              <WorkflowRunsPage />
            </Suspense>
          </RequireRole>
        ),
        handle: {
          breadcrumb: 'workflows.runs.title',
        } satisfies BreadcrumbHandle,
      },
      // W66-F5-S2 — single run drilldown (top-level `/workflow-runs/:runId`
      // matching the backend's `GET /workflow-runs/:runId` shape).
      {
        path: '/workflow-runs/:runId',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <Suspense fallback={<PageSuspenseFallback />}>
              <WorkflowRunDetailPage />
            </Suspense>
          </RequireRole>
        ),
        handle: {
          breadcrumb: 'workflows.runs.detail.breadcrumb',
        } satisfies BreadcrumbHandle,
      },
      // W19-F7-S1 — EU Taxonomy per-asset evaluation route. The optional
      // `:evaluationId?` lets the page handle three states: no
      // evaluation yet, an open evaluation, and a closed evaluation.
      {
        path: '/buildings/:buildingId/eu-taxonomy/:evaluationId?',
        element: (
          <Suspense fallback={<PageSuspenseFallback />}>
            <EuTaxonomyPage />
          </Suspense>
        ),
        handle: {
          breadcrumb: 'module.euTaxonomy.title',
        } satisfies BreadcrumbHandle,
      },
      // W929-S6 — LCA portfolio rollup route.
      {
        path: '/portfolios/:portfolioId/lca',
        element: (
          <Suspense fallback={<PageSuspenseFallback />}>
            <LcaPortfolioPage />
          </Suspense>
        ),
        handle: {
          breadcrumb: 'lca.portfolio.title',
        } satisfies BreadcrumbHandle,
      },
      // W19-F7-S1 — EU Taxonomy portfolio rollup route.
      {
        path: '/portfolios/:portfolioId/eu-taxonomy',
        element: (
          <Suspense fallback={<PageSuspenseFallback />}>
            <EuTaxonomyPortfolioPage />
          </Suspense>
        ),
        handle: {
          breadcrumb: 'module.euTaxonomy.portfolio.title',
        } satisfies BreadcrumbHandle,
      },
      // W19-F7-S1 — EU Taxonomy Orders admin page.
      {
        path: '/admin/eu-taxonomy-orders',
        element: (
          <Suspense fallback={<PageSuspenseFallback />}>
            <EuTaxonomyOrdersPage />
          </Suspense>
        ),
        handle: {
          breadcrumb: 'module.euTaxonomy.orders.title',
        } satisfies BreadcrumbHandle,
      },
      // W47-F6-S1 — generic cross-tenant Orders landing + detail.
      {
        path: '/admin/orders',
        element: (
          <Suspense fallback={<PageSuspenseFallback />}>
            <AdminOrdersListPage />
          </Suspense>
        ),
        handle: {
          breadcrumb: 'admin.orders.title',
        } satisfies BreadcrumbHandle,
      },
      {
        path: '/admin/orders/:id',
        element: (
          <Suspense fallback={<PageSuspenseFallback />}>
            <AdminOrderDetailPage />
          </Suspense>
        ),
        handle: {
          breadcrumb: 'admin.orders.detail.title',
        } satisfies BreadcrumbHandle,
      },
      // W133-F3-S2 — AI token spend dashboard (partner_admin / platform_admin).
      {
        path: '/admin/ai-cost',
        element: (
          <Suspense fallback={<PageSuspenseFallback />}>
            <AiCostPage />
          </Suspense>
        ),
        handle: {
          breadcrumb: 'aiCost.title',
        } satisfies BreadcrumbHandle,
      },
      // #1647 — AI-assisted data ingestion front door. Admin-gated so the
      // upload + classify surface only renders for users who can act on it.
      {
        path: '/admin/ingest',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <Suspense fallback={<PageSuspenseFallback />}>
              <IngestPage />
            </Suspense>
          </RequireRole>
        ),
        handle: {
          breadcrumb: 'nav.adminIngest',
        } satisfies BreadcrumbHandle,
      },
      // W137-F1-S2 — utility connector registry (partner_admin / platform_admin).
      {
        path: '/admin/utility-connectors',
        element: (
          <Suspense fallback={<PageSuspenseFallback />}>
            <ConnectorsAdminPage />
          </Suspense>
        ),
        handle: {
          breadcrumb: 'utilityConnectors.title',
        } satisfies BreadcrumbHandle,
      },
      // W143-F6 — utility connector ops console (platform_admin only).
      {
        path: '/admin/utility-connectors/ops',
        element: (
          <Suspense fallback={<PageSuspenseFallback />}>
            <ConnectorOpsPage />
          </Suspense>
        ),
        handle: {
          breadcrumb: 'connectorOps.title',
        } satisfies BreadcrumbHandle,
      },
      // W146-F3-S1 — partner analytics (engagement + health). partner_admin / platform_admin only.
      {
        path: '/admin/partner-analytics',
        element: (
          <RequireRole
            roles={['partner_admin', 'platform_admin']}
            fallback={<ForbiddenPage />}
          >
            <Suspense fallback={<PageSuspenseFallback />}>
              <PartnerAnalyticsPage />
            </Suspense>
          </RequireRole>
        ),
        handle: {
          breadcrumb: 'partnerAnalytics.title',
        } satisfies BreadcrumbHandle,
      },
      // Canonical 404 catch-all per
      // `sustevo-handbook/brand/ERROR_PAGES.md` § 1. Lives INSIDE the
      // AppShell route group so the chrome stays around the EmptyState
      // — user knows where they are, just hit a bad URL.
      { path: '*', element: <NotFoundPage /> },
      // W102-F3-S2 — Cross-org deep-link 403 recovery page. Reached
      // when the API returns `{ code: 'CROSS_ORG_RESOURCE' }`;
      // ActiveOrgErrorBridge navigates here with the required org id
      // in the URL param and the original target in `?returnTo=`.
      {
        path: '/errors/cross-org/:requiredOrgId',
        element: (
          <Suspense fallback={null}>
            <CrossOrgForbidden />
          </Suspense>
        ),
      },
    ],
  },
  // W79 PR B — tenant portal (). Sign-in route is public
  // (renders the form regardless of session); every other tenant route
  // mounts inside TenantLayout, which redirects to /tenant/sign-in
  // when no tenant session exists. Backend @TenantOnly() guards stay
  // the authoritative tier check.
  {
    path: '/tenant/sign-in',
    element: (
      <Suspense fallback={<PageSuspenseFallback />}>
        <TenantSignInPage />
      </Suspense>
    ),
  },
  {
    path: '/tenant',
    element: (
      <Suspense fallback={<PageSuspenseFallback />}>
        <TenantLayoutLazy />
      </Suspense>
    ),
    children: [
      { index: true, element: <TenantHomePage /> },
      { path: 'requests', element: <TenantRequestsPage /> },
      { path: 'requests/new', element: <TenantNewRequestPage /> },
      { path: 'transparency', element: <TenantTransparencyPage /> },
      {
        path: 'esg/:sectionKind',
        element: <TenantEsgSectionDeepDivePage />,
      },
      { path: 'amenities', element: <TenantAmenitiesPage /> },
      { path: 'workflows', element: <TenantWorkflowsPage /> },
      { path: 'workflows/new', element: <TenantWorkflowsPage /> },
    ],
  },
  // The catch-all `<Navigate to="/" replace />` was removed 2026-05-21
  // per `sustevo-handbook/brand/ERROR_PAGES.md` § 1 (anti-pattern:
  // silent redirect on a bad URL). The canonical 404 now renders
  // inside the AppShell route group above.
]);
